/**
 * US Federal Holidays, as *observed* dates (YYYY-MM-DD, server-local calendar days).
 *
 * A business day for the Day Streak is any weekday that is not listed here.
 *
 * The list is computed for the previous, current and next calendar year from
 * the statutory rules (fixed dates, or an nth weekday of a month). Fixed dates
 * follow the federal observance rule: Saturday → previous Friday,
 * Sunday → following Monday (e.g. Jul 4 2026 is a Saturday → observed Jul 3).
 */

/** Local calendar day as YYYY-MM-DD. */
function toKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** The `nth` (1-based) `weekday` of `month` (0-based) in `year`. */
function nthWeekday(year: number, month: number, weekday: number, nth: number): Date {
  const first = new Date(year, month, 1);
  return new Date(year, month, 1 + ((weekday - first.getDay() + 7) % 7) + (nth - 1) * 7);
}

/** The last `weekday` of `month` (0-based) in `year`. */
function lastWeekday(year: number, month: number, weekday: number): Date {
  const last = new Date(year, month + 1, 0); // day 0 of next month = last day of this one
  return new Date(year, month, last.getDate() - ((last.getDay() - weekday + 7) % 7));
}

/** Fixed-date holidays shift off the weekend: Sat → Fri, Sun → Mon. */
function observed(date: Date): Date {
  const day = date.getDay();
  if (day === 6) return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
  if (day === 0) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return date;
}

const MONDAY = 1;
const THURSDAY = 4;

/** Every federal holiday whose statutory date falls in `year`, as observed. */
function holidaysForYear(year: number): string[] {
  return [
    observed(new Date(year, 0, 1)), // New Year's Day
    nthWeekday(year, 0, MONDAY, 3), // Martin Luther King Jr. Day
    nthWeekday(year, 1, MONDAY, 3), // Presidents' Day
    lastWeekday(year, 4, MONDAY), // Memorial Day
    observed(new Date(year, 5, 19)), // Juneteenth
    observed(new Date(year, 6, 4)), // Independence Day
    nthWeekday(year, 8, MONDAY, 1), // Labor Day
    nthWeekday(year, 9, MONDAY, 2), // Columbus Day
    observed(new Date(year, 10, 11)), // Veterans Day
    nthWeekday(year, 10, THURSDAY, 4), // Thanksgiving Day
    observed(new Date(year, 11, 25)), // Christmas Day
  ].map(toKey);
}

const CURRENT_YEAR = new Date().getFullYear();

/** Previous, current and next calendar year, sorted. */
export const FEDERAL_HOLIDAYS: readonly string[] = [
  ...holidaysForYear(CURRENT_YEAR - 1),
  ...holidaysForYear(CURRENT_YEAR),
  ...holidaysForYear(CURRENT_YEAR + 1),
].sort();

export const FEDERAL_HOLIDAY_SET: ReadonlySet<string> = new Set(FEDERAL_HOLIDAYS);